

// Order priority tags
export type OrderTag = 'low' | 'medium' | 'high' | 'urgent';

// Order line delivery tags
export type OrderLineDeliveryTag = 'delivery_partial' | 'delivery_complete';

// Order domain types (matching backend API)
export interface Order {
  id: string;
  code: string; // Order ID like "ORD001"
  order_parent_id?: string; // Parent order code (for child orders)
  order_child_ids?: string[]; // Child order codes (for parent orders)
  customer_id: string;
  customer_price_id?: string;
  order_source: OrderSource;
  current_status: StatusHistory;
  tag?: OrderTag;
  note?: string;
  order_lines_id: string[]; // Array of order line IDs
  shipping_address?: string;
  shipping_at?: string;
  total_price: number;
  status_history: StatusHistory[];

  // Tracking fields
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// Status history for tracking order progress
export interface StatusHistory {
  status: OrderStatus;
  note?: string;
  createdBy: string;
  createdAt: string;
  delivered_quantity?: number;
  total_quantity?: number;
  pending_completion?: boolean; // Đã giao đủ nhưng chờ đủ NVL để hoàn thành
}

// Order line interface (for UI display)
export interface OrderLine {
  id: string;
  code: string; // Order line code
  orderId: string;
  customerId: string;
  customer_name?: string; // Customer name for display
  customer_phone?: string; // Customer phone for display
  serviceId: string;
  serviceName?: string; // Will be populated from service data
  serviceCode?: string; // Will be populated from service data
  unit?: string; // Will be populated from service data
  quantity: number;
  unitPrice: number; // Price per unit
  priceSource: string; // Source of the price
  discountPercentage: number; // Discount as percentage (0-100)
  discount: number; // Calculated discount amount
  sellingPrice: number; // Final selling price per unit
  totalPrice: number; // Final total price after discount

  // Rework tracking fields - NEW
  actual_quantity?: number; // Number of units that passed (0 if not yet confirmed)
  error_quantity?: number; // Number of units that failed (0 if not yet confirmed)
  type?: 'normal' | 'rework'; // OrderLine type
  parent_order_line_id?: string; // Parent order line code (only for rework lines)
  child_order_line_ids?: string[]; // Child rework order line codes
  rework_generation?: number; // 0=original, 1=first rework, etc
  error_from_step?: string; // Step where error occurred (only for rework lines)
  exclude_from_invoice?: boolean; // true for rework lines
  inventoryStatus: string; // Inventory status
  currentStatus: StatusHistory; // Current status object
  statusHistory: StatusHistory[]; // Status history array
  currentTag?: TagHistory; // Current tag object
  tagHistory?: TagHistory[]; // Tag history array
  notes?: string;

  // Delivery assignments - NEW
  deliveryAssignments?: DeliveryAssignment[]; // Array of driver assignments

  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// Delivery assignment for order lines
export interface DeliveryAssignment {
  driverId: string; // User ID of driver
  driverName: string; // Driver full name
  assignedQuantity: number; // Quantity assigned to this driver
  deliveredQuantity: number; // Quantity delivered by this driver
  status: DeliveryAssignmentStatus; // Assignment status
  assignedAt: string; // Assignment timestamp
  assignedBy: string; // User who assigned
  note?: string; // Assignment notes
}

export type DeliveryAssignmentStatus = 'assigned' | 'delivering' | 'completed';

export interface DeliveryAssignmentInput {
  driverId: string;
  assignedQuantity: number;
  note?: string;
}

// Tag history interface for order lines
export interface TagHistory {
  tag: string;
  note?: string;
  createdAt: string;
  createdBy: string;
}

// Child order summary for parent order responses
export interface ChildOrderSummary {
  code: string;
  status: OrderStatus;
  total_price: number;
  order_lines?: OrderLine[]; // Order lines for the child order
  createdAt: string;
}

// Parent order summary for child order responses
export interface ParentOrderSummary {
  code: string;
  status: OrderStatus;
  customer_id: string;
}

// UI-friendly order interface (transformed from backend)
export interface OrderUI {
  id: string;
  code: string;
  invoiceId?: string; // Invoice ID if order has associated invoice
  orderParentId?: string; // Parent order code (for child orders)
  orderChildIds?: string[]; // Child order codes (for parent orders)
  childCount?: number; // Number of child orders (for list responses)
  childOrders?: OrderUI[]; // Full child order details (for detail responses)
  parentOrder?: ParentOrderSummary; // Parent order summary (for child order detail responses)
  customerId: string;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
  orderSource: OrderSource;
  status: OrderStatus;
  tag?: OrderTag;
  notes?: string;
  orderLines: OrderLine[];
  totalPrice: number;
  shippingAddress?: string;
  shippingAt?: string;
  deliveryAt?: string; // Delivery date and time
  createdBy: string;
  createdByName?: string; // Name of user who created the order
  createdAt: string;
  updatedAt: string;
  statusHistory: StatusHistory[];
}

// Order status workflow: Created → Confirmed → Processing → Delivery → Completed
// Also includes order line statuses for compatibility
export type OrderStatus = 'created' | 'confirmed' | 'processing' | 'in_production' | 'delivery' | 'completed' | 'delivered' | 'cancelled' | 'waiting_confirm' | 'waiting_print' | 'waiting_print_check' | 'waiting_laminate' | 'waiting_cut' | 'waiting_delivery';

// Order sources
export type OrderSource = 'website' | 'crm' | 'mobile_app' | 'zalo' | 'email' | 'in_person' | 'facebook' | 'tiktok' | 'shopee' | 'lazada' | 'phone' | 'walk_in' | 'other';

// Delivery progress tracking
export interface DeliveryProgress {
  status: OrderStatus;
  percentage: number; // 0-100
  currentStage: string;
  estimatedDelivery?: string;
  actualDelivery?: string;
}

// Form interfaces for creating/editing orders (matching backend API)
export interface CreateOrderRequest {
  customer_id: string;
  customer_price_id?: string;
  order_source: OrderSource;
  note?: string;
  tag?: OrderTag;
  order_lines?: CreateOrderLineRequest[]; // Array of order lines to create with the order
  order_lines_id?: string[]; // Array of existing order line IDs (deprecated, use order_lines instead)
  shipping_address?: string;
  shipping_at?: string; // ISO date string
  created_at?: string; // ISO date string - Order creation date
  delivery_at?: string; // ISO date string - Delivery date and time
}

// Child order creation request
export interface CreateChildOrderRequest {
  order_lines?: CreateOrderLineRequest[]; // Array of order lines to create with the child order
  note?: string;
  order_source?: OrderSource; // Inherited from parent if not provided
  shipping_at?: string; // Inherited from parent if not provided
  shipping_address?: string; // Inherited from parent if not provided
  tag?: OrderTag; // Inherited from parent if not provided
}

// Duplicate order request
export interface DuplicateOrderRequest {
  customer_id?: string; // Inherited from original if not provided
  customer_price_id?: string; // Inherited from original if not provided
  note?: string; // Inherited from original if not provided
  order_source?: OrderSource; // Inherited from original if not provided
  shipping_at?: string; // Inherited from original if not provided
  shipping_address?: string; // Inherited from original if not provided
  tag?: OrderTag; // Inherited from original if not provided
  order_lines?: CreateOrderLineRequest[]; // Custom order lines with notes
}

// Order line creation request for use within order creation
export interface CreateOrderLineRequest {
  service_id: string;
  quantity: number;
  source_price?: string;
  discount_percentage?: number;
  selling_price?: number;
  note?: string; // Notes for the order line
}

export interface UpdateOrderRequest {
  customer_id?: string;
  customer_price_id?: string;
  order_source?: OrderSource;
  note?: string;
  tag?: OrderTag;
  order_lines_id?: string[];
  shipping_address?: string;
  shipping_at?: string;
  created_at?: string; // ISO date string - Order creation date
  delivery_at?: string; // ISO date string - Delivery date and time
}

export interface UpdateOrderLineRequest {
  id?: string; // Optional for new lines
  serviceId: string;
  quantity: number;
  unitPrice: number;
  discountPercentage: number;
  notes?: string;
}

// Filter and search interfaces (matching backend API)
export interface OrderFilters {
  search?: string; // Search term for filtering
  customer_id?: string; // Filter by customer
  customer_price_id?: string; // Filter by customer price
  order_source?: OrderSource | OrderSource[]; // Filter by order source (single or multiple)
  current_status?: OrderStatus; // Filter by current status
  status?: OrderStatus[]; // Filter by multiple statuses
  tag?: OrderTag | OrderTag[]; // Filter by tag (single or multiple)
  printing_room?: string[]; // Filter by printing room (for Admin/Truong quay only)
  shipping_from?: string; // Filter by shipping date from (RFC3339 format)
  shipping_to?: string; // Filter by shipping date to (RFC3339 format)
  min_total_price?: number | string; // Filter by minimum total price
  max_total_price?: number | string; // Filter by maximum total price
  no_invoice?: boolean; // Filter for orders without invoices
  parent_only?: boolean; // Filter for parent orders only
  child_only?: boolean; // Filter for child orders only
  sort_by?: string; // Field to sort by
  sort_order?: 'asc' | 'desc'; // Sort order
}

// API response interfaces
export interface OrderListResponse {
  data: OrderUI[];
  total: number;
  page: number;
  pageSize: number;
}

export interface OrderDetailResponse {
  order: OrderUI;
  customer: {
    id: string;
    name: string;
    phone?: string;
    email?: string;
    address?: string;
  };
  orderLines: OrderLine[];
  invoice?: {
    id: string;
    code: string;
    status: string;
    total: number;
  };
  statusHistory: StatusHistory[];
}

// Legacy interface for backward compatibility
export interface OrderStatusHistory {
  id: string;
  orderId: string;
  fromStatus?: OrderStatus;
  toStatus: OrderStatus;
  changedBy: string;
  changedAt: string;
  notes?: string;
}

// Statistics and dashboard data
export interface OrderStatsResponse {
  totalOrders: number;
  createdOrders: number;
  confirmedOrders: number;
  inProductionOrders: number;
  deliveredOrders: number;
  cancelledOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  topCustomers: Array<{
    customerId: string;
    customerName: string;
    orderCount: number;
    totalValue: number;
  }>;
}

// UI state interfaces
export interface OrderColumnVisibility {
  code: boolean;
  customer: boolean;
  responsiblePerson: boolean;
  orderSource: boolean;
  status: boolean;
  deliveryProgress: boolean;
  total: boolean;
  amountDue: boolean;
  tags: boolean;
  createdBy: boolean;
  createdAt: boolean;
  actions: boolean;
}

// Type counts for filter tabs
export interface OrderTypeCounts {
  all: number;
  created: number;
  confirmed: number;
  in_production: number;
  delivered: number;
  cancelled: number;
}

// Pricing context for order lines
export interface OrderLinePricing {
  serviceId: string;
  customerId: string;
  basePrice: number;
  customerPrice?: number; // Customer-specific price
  groupPrice?: number; // Customer group price
  finalPrice: number; // Price to use (customer > group > base)
  discountPercentage: number;
  finalTotal: number;
}

// Order workflow and permissions
export interface OrderWorkflow {
  orderId: string;
  currentStatus: OrderStatus;
  canEdit: boolean; // Based on invoice attachment and permissions
  canConfirm: boolean;
  canCancel: boolean;
  canDelete: boolean;
  availableTransitions: OrderStatus[];
  restrictionReason?: string; // Why certain actions are restricted
}

// Export and reporting options
export interface ExportOrderOptions {
  dateRange: 'all' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'custom';
  customDateFrom?: string;
  customDateTo?: string;
  includeOrderLines: boolean;
  includeCustomerDetails: boolean;
  includeFinancialDetails: boolean;
  format: 'excel' | 'pdf' | 'csv';
  columns?: string[];
}

// Form validation interfaces
export interface OrderFormErrors {
  customerId?: string;
  orderSource?: string;
  orderLines?: Array<{
    serviceId?: string;
    quantity?: string;
    unitPrice?: string;
    discountPercentage?: string;
  }>;
  general?: string;
}

// Constants for UI - Note: This is deprecated, use i18n translations instead
// Use t('orders.status.{status}') for proper internationalization
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  created: 'Đã tạo',
  confirmed: 'Đã xác nhận',
  processing: 'Đang xử lý',
  in_production: 'Đang sản xuất',
  delivery: 'Đang giao hàng',
  completed: 'Hoàn thành',
  delivered: 'Đã giao hàng',
  cancelled: 'Đã hủy',
  waiting_confirm: 'Chờ xác nhận',
  waiting_print: 'Chờ in',
  waiting_print_check: 'Chờ kiểm tra in',
  waiting_laminate: 'Chờ cán màng',
  waiting_cut: 'Chờ cắt',
  waiting_delivery: 'Chờ giao hàng',
};

export const ORDER_TAG_LABELS: Record<OrderTag, string> = {
  low: 'Thấp',
  medium: 'Trung bình',
  high: 'Cao',
  urgent: 'Khẩn cấp',
};

export const ORDER_LINE_DELIVERY_TAG_LABELS: Record<OrderLineDeliveryTag, string> = {
  delivery_partial: 'Giao hàng một phần',
  delivery_complete: 'Hoàn thành giao hàng',
};

export const ORDER_SOURCE_LABELS: Record<OrderSource, string> = {
  website: 'Website',
  crm: 'CRM',
  mobile_app: 'Mobile App',
  zalo: 'Zalo',
  email: 'Email',
  in_person: 'Trực tiếp',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  shopee: 'Shopee',
  lazada: 'Lazada',
  phone: 'Điện thoại',
  walk_in: 'Tại cửa hàng',
  other: 'Khác',
};

// Status colors for UI
export const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  created: 'bg-gray-100 text-gray-800',
  confirmed: 'bg-blue-100 text-blue-800',
  processing: 'bg-yellow-100 text-yellow-800',
  in_production: 'bg-orange-100 text-orange-800',
  delivery: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  waiting_confirm: 'bg-yellow-100 text-yellow-800',
  waiting_print: 'bg-blue-100 text-blue-800',
  waiting_print_check: 'bg-orange-100 text-orange-800',
  waiting_laminate: 'bg-purple-100 text-purple-800',
  waiting_cut: 'bg-orange-100 text-orange-800',
  waiting_delivery: 'bg-yellow-100 text-yellow-800',
};

// Tag colors for UI
export const ORDER_TAG_COLORS: Record<OrderTag, string> = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800',
};

// Delivery tag colors for UI
export const ORDER_LINE_DELIVERY_TAG_COLORS: Record<OrderLineDeliveryTag, string> = {
  delivery_partial: 'bg-yellow-100 text-yellow-800',
  delivery_complete: 'bg-green-100 text-green-800',
};

// Source colors for UI
export const ORDER_SOURCE_COLORS: Record<OrderSource, string> = {
  website: 'bg-purple-100 text-purple-800',
  crm: 'bg-blue-100 text-blue-800',
  mobile_app: 'bg-green-100 text-green-800',
  zalo: 'bg-blue-100 text-blue-800',
  email: 'bg-orange-100 text-orange-800',
  in_person: 'bg-gray-100 text-gray-800',
  facebook: 'bg-blue-100 text-blue-800',
  tiktok: 'bg-pink-100 text-pink-800',
  shopee: 'bg-orange-100 text-orange-800',
  lazada: 'bg-blue-100 text-blue-800',
  phone: 'bg-green-100 text-green-800',
  walk_in: 'bg-gray-100 text-gray-800',
  other: 'bg-gray-100 text-gray-800',
};

// Order status history entry for API responses (different from invoice status)
export interface OrderStatusHistoryResponse {
  status: string; // Using string to accommodate all order statuses
  note?: string;
  createdAt: string;
  createdBy: string;
  delivered_quantity?: number;
  total_quantity?: number;
  pending_completion?: boolean;
}

// Order response structure matching the API
export interface OrderResponse {
  id: string;
  code: string;
  invoice_id: string;
  order_parent_id?: string; // Parent order code (for child orders)
  order_child_ids?: string[]; // Child order codes (for parent orders)
  child_count?: number; // Number of child orders (for list responses)
  child_orders?: OrderResponse[]; // Full child order details (for detail responses)
  parent_order?: ParentOrderSummary; // Parent order summary (for child order detail responses)
  customer_id: string;
  customer?: any; // Complete customer information from API
  customer_price_id?: string;
  order_lines_id: string[];
  order_lines?: OrderLineResponse[]; // Complete order line details from API
  note?: string;
  order_source: string;
  shipping_at?: string;
  shipping_address?: string;
  delivery_at?: string; // Delivery date and time
  current_status: OrderStatusHistoryResponse;
  status_history: OrderStatusHistoryResponse[];
  tag?: string;
  total_price: number;
  createdBy: string;
  created_by_name?: string; // Name of user who created the order
  createdAt: string;
  updatedAt: string;
}

// Order line response structure matching the backend API
export interface OrderLineResponse {
  id: string;
  code: string;
  order_id: string;
  customer_id: string;
  customer_name?: string; // Customer name for display
  customer_phone?: string; // Customer phone for display
  service_id: string;
  serviceName?: string; // Service name for display
  serviceUnit?: string; // Service unit for display
  service?: any; // Complete service information from API
  quantity: number;
  unit_price: number;
  price_source: string;
  discount_percentage: number;
  discount: number;
  selling_price: number;
  total_price: number;
  inventory_status: string;
  current_status: OrderStatusHistoryResponse;

  // Rework tracking fields - NEW
  actual_quantity?: number;
  error_quantity?: number;
  type?: 'normal' | 'rework';
  parent_order_line_id?: string;
  child_order_line_ids?: string[];
  rework_generation?: number;
  error_from_step?: string;
  exclude_from_invoice?: boolean;
  status_history: OrderStatusHistoryResponse[];
  current_tag?: TagHistoryResponse;
  tag_history?: TagHistoryResponse[];
  note?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;

  // Delivery assignments - NEW
  delivery_assignments?: Array<{
    driver_id: string;
    driver_name: string;
    assigned_quantity: number;
    delivered_quantity: number;
    status: string;
    assigned_at: string;
    assigned_by: string;
    note?: string;
  }>;
}

// Tag history response structure matching the backend API
export interface TagHistoryResponse {
  tag: string;
  note?: string;
  createdAt: string;
  createdBy: string;
}


// Response for grouped order lines
export interface GroupedOrderLinesResponse {
  order: OrderUI;
  order_lines: OrderLine[];
  total_lines: number;
}

// Rework System - NEW Types
export interface ConfirmStepRequest {
  actual_quantity: number; // Number of units that passed this step
  error_quantity: number; // Number of units that failed this step
  note?: string; // Notes about errors or completion
}

export interface ConfirmStepResponse {
  original_order_line: OrderLineResponse; // Updated original order line
  rework_order_line?: OrderLineResponse; // Newly created rework order line (only if error_quantity > 0)
}

export interface OrderLineTreeResponse {
  root: OrderLineResponse;
  children: OrderLineResponse[];
  total_children: number;
}

export interface ReworkStatistics {
  total_order_lines: number;
  normal_order_lines: number;
  rework_order_lines: number;
  total_units: number;
  error_units: number;
  rework_rate: number; // Percentage
}

// ============================================
// Daily Delivery Types
// ============================================

export interface DailyDeliveryListQuery {
  date?: string; // Format: YYYY-MM-DD, defaults to today
  include_overdue?: boolean; // Include overdue orders, defaults to true
  driver_id?: string; // Filter by driver
  status?: string; // Comma-separated statuses: waiting_delivery,delivery
  page?: number;
  page_size?: number;
}

export interface DailyDeliveryItem {
  code: string;
  order_code: string;
  order_delivery_at: string;
  customer_code: string;
  customer_name: string;
  customer_address: string;
  quantity: number;
  service_name: string;
  delivery_assignments: DeliveryAssignmentResponse[];
  current_status: DailyDeliveryStatusResponse;
  note: string;
  is_overdue: boolean;
}

export interface DeliveryAssignmentResponse {
  driver_id: string;
  driver_name: string;
  assigned_quantity: number;
  delivered_quantity: number;
  status: DeliveryAssignmentStatus;
  assigned_at: string;
  assigned_by: string;
  note?: string;
}

export interface MyDeliveryResponse {
  order_line_code: string;
  order_code: string;
  customer_name: string;
  customer_address: string;
  service_name: string;
  my_assignment: DeliveryAssignmentResponse;
}

export interface DailyDeliveryStatusResponse {
  status: string;
  note?: string;
  createdAt: string;
  createdBy: string;
  delivered_quantity?: number;
  total_quantity?: number;
  pending_completion?: boolean;
}

export interface DailyDeliverySummary {
  total_orders: number;
  overdue_count: number;
  waiting_delivery: number;
  delivering: number;
  ready_to_deliver: number;
}

export interface PaginationResponse {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface DailyDeliveryResponse {
  data: DailyDeliveryItem[];
  summary: DailyDeliverySummary;
  pagination: PaginationResponse;
}
